import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_LOGO_URL } from "../../config/branding";
import { logout } from "../../utils/authUtils";
import { useDemoMode } from "../../contexts/DemoModeContext";
import { getPortalAdminTools } from "./portalAdminTools";
import "./PortalAdminLayout.css";

const PortalAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [decodedRoles, setDecodedRoles] = useState([]);
  const { isDemoMode } = useDemoMode();
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").toLowerCase();
  const isDemoEnvironment =
    !apiBaseUrl ||
    apiBaseUrl.includes("api-demo") ||
    apiBaseUrl.includes("localhost") ||
    apiBaseUrl.includes("127.0.0.1");
  const showDemoTools = isDemoMode && isDemoEnvironment;

  useEffect(() => {
    // Check portal admin access
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        decoded.role === "R8" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6") ||
            decoded.roles.includes("R8")));

      if (!hasPortalAdminAccess) {
        navigate("/dashboard");
        return;
      }

      setDecodedRoles(
        Array.isArray(decoded.roles) && decoded.roles.length
          ? decoded.roles
          : decoded.role
            ? [decoded.role]
            : [],
      );
    } catch (error) {
      navigate("/login");
    }
  }, [navigate]);

  const menuItems = getPortalAdminTools({ showDemoTools, userRoles: decodedRoles }).filter(
    (item) => item.showInSidebar && item.available !== false,
  );

  const handleLogout = () => {
    logout(); // This now includes CRM widget reset
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="portal-admin-layout">
      {/* Sidebar */}
      <aside
        className={`portal-sidebar ${isMobileMenuOpen ? "mobile-open" : ""}`}
      >
        <div className="portal-sidebar-header">
          <div className="portal-logo">
            <img
              src={APP_LOGO_URL}
              alt="OGESTYLE Logo"
              style={{ height: "30px", width: "auto", marginRight: "10px" }}
            />
            <h2>Portal Admin</h2>
          </div>
        </div>

        <nav className="portal-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => !item.disabled && navigate(item.path)}
              className={`portal-nav-item ${isActive(item.path) ? "active" : ""} ${item.disabled ? "disabled" : ""}`}
              disabled={item.disabled}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {item.disabled && <span className="coming-soon">Soon</span>}
            </button>
          ))}
        </nav>

        <div className="portal-sidebar-footer">
          <button
            onClick={() => window.open("/", "_blank")}
            className="portal-nav-item"
          >
            <span className="nav-icon">🏠</span>
            <span className="nav-label">Go to Home</span>
            <span className="external-icon">↗</span>
          </button>
          <button onClick={handleLogout} className="portal-nav-item logout">
            <span className="nav-icon">🚪</span>
            <span className="nav-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile menu button */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? "✕" : "☰"}
      </button>

      {/* Main content */}
      <main className="portal-main-content">{children}</main>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default PortalAdminLayout;
